/**
 * Chat Member Events Hook
 * Listens to WebSocket events for member management and invalidates cache
 */

import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useChatSocket } from './useChatSocket';
import { chatKeys } from './useChat';
import { message } from 'antd';
import type {
  ChatMemberAddedEvent,
  ChatMemberRemovedEvent,
  ChatMemberRoleChangedEvent,
} from '@/api/types/chat.types';

interface UseChatMemberEventsOptions {
  /** Enable WebSocket event listeners */
  enabled?: boolean;
  /** Show toast notifications for member events */
  showNotifications?: boolean;
}

/**
 * Hook to listen for chat member events and update React Query cache
 *
 * @example
 * ```tsx
 * // In ChatMembersPanel or ChatListSidebar
 * useChatMemberEvents({
 *   enabled: true,
 *   showNotifications: true,
 * });
 * ```
 */
export function useChatMemberEvents(options: UseChatMemberEventsOptions = {}) {
  const { enabled = true, showNotifications = false } = options;
  const queryClient = useQueryClient();

  // Handler for member added event
  const handleMemberAdded = useCallback(
    (event: ChatMemberAddedEvent) => {
      console.log('👤 Member added:', event.data);

      const { chat_id, display_name, added_by_name } = event.data;

      // Invalidate members list for this chat
      queryClient.invalidateQueries({ queryKey: chatKeys.members(chat_id) });

      // Invalidate chat detail to update participant_count
      queryClient.invalidateQueries({ queryKey: chatKeys.detail(chat_id) });

      // Invalidate chat list to refresh in sidebar
      queryClient.invalidateQueries({ queryKey: chatKeys.lists() });

      // Show notification
      if (showNotifications) {
        message.success(`${display_name} was added by ${added_by_name}`);
      }
    },
    [queryClient, showNotifications]
  );

  // Handler for member removed event
  const handleMemberRemoved = useCallback(
    (event: ChatMemberRemovedEvent) => {
      console.log('👤 Member removed:', event.data);

      const { chat_id, user_id, removed_by_name } = event.data;

      // Invalidate members list for this chat
      queryClient.invalidateQueries({ queryKey: chatKeys.members(chat_id) });

      // Invalidate chat detail to update participant_count
      queryClient.invalidateQueries({ queryKey: chatKeys.detail(chat_id) });

      // Invalidate chat list to refresh in sidebar
      queryClient.invalidateQueries({ queryKey: chatKeys.lists() });

      // Show notification
      if (showNotifications) {
        message.info(`Member was removed by ${removed_by_name}`);
      }
    },
    [queryClient, showNotifications]
  );

  // Handler for member role changed event
  const handleMemberRoleChanged = useCallback(
    (event: ChatMemberRoleChangedEvent) => {
      console.log('👤 Member role changed:', event.data);

      const { chat_id, old_role, new_role, changed_by_name } = event.data;

      // Invalidate members list for this chat
      queryClient.invalidateQueries({ queryKey: chatKeys.members(chat_id) });

      // Invalidate chat detail to update my_role if it was changed
      queryClient.invalidateQueries({ queryKey: chatKeys.detail(chat_id) });

      // Show notification
      if (showNotifications) {
        message.info(
          `Role changed from ${old_role} to ${new_role} by ${changed_by_name}`
        );
      }
    },
    [queryClient, showNotifications]
  );

  // Connect to WebSocket and set up event handlers
  useChatSocket({
    enabled,
    handlers: {
      onMemberAdded: handleMemberAdded,
      onMemberRemoved: handleMemberRemoved,
      onMemberRoleChanged: handleMemberRoleChanged,
    },
  });

  return {
    // No return values needed - this hook is for side effects only
  };
}
